import React, { memo, useDeferredValue, useMemo, useRef, useState } from 'react';
import { Box, Input, useToast, InputGroup, InputRightElement, useDisclosure, Spinner } from '@chakra-ui/react';
import { useMutation, useApolloClient } from '@apollo/client';
import { ChangeTimeZone } from '../../../graphql/mutations/ChangeTimeZone';
import { useNavigate } from 'react-router-dom';
import { useError } from '../../../error-provider/ErrorProvider';
import { PATHS } from '../../../router/routes';
import { variants } from '../../../utils/constants';
import { useGetMeQuery, useGetTimeZonesQuery } from 'graphql/generated';
import { useUserData } from 'context/UserDataProvider';
import ExpandUp from 'components/custom-components/icons/ExpandUp';
import ExpandDown from 'components/custom-components/icons/ExpandDown';
import { useClickOutside } from 'hooks/useClickOutside';

interface Props {
    agentId: string;
    timeZone: string;
}

const TimeZone = ({ agentId, timeZone }: Props) => {
    const { isOpen, onClose, onOpen } = useDisclosure();
    const [selectedValue, setSelectedValue] = useState(timeZone);
    const [searchTerms, setSearchTerms] = useState('');

    const toast = useToast();
    const { showError } = useError();
    const router = useNavigate();
    const boxRef = useRef<HTMLDivElement>(null);

    const { data } = useGetTimeZonesQuery({
        onError(error) {
            showError(undefined, error);
        },
    });

    const apolloClient = useApolloClient();
    const { fetchUserData } = useUserData();

    const { data: meData } = useGetMeQuery({
        onError(error) {
            showError(undefined, error);
        },
    });
    const [changeTimeZone, { loading }] = useMutation(ChangeTimeZone, {
        onError(error) {
            showError(undefined, error);
        },
    });

    useClickOutside(boxRef, onClose);

    const timeZones = useMemo(() => {
        return data?.getTimeZones?.listTimeZones ?? [];
    }, [data]);

    const deferredQuery = useDeferredValue(searchTerms);

    function normalizeString(str: string) {
        return str.replace(/[_-]/g, ' ').replace(/\s+/g, ' ').toLowerCase();
    }

    const filteredTimeZones = useMemo(() => {
        if (!deferredQuery.trim()) {
            return timeZones;
        }

        const normalizedQuery = normalizeString(deferredQuery);
        return timeZones.filter((tz: string) => {
            const normalizedTz = normalizeString(tz);
            return normalizedTz.includes(normalizedQuery);
        });
    }, [deferredQuery, timeZones]);

    const userId = meData?.getMe.__typename === 'UserModelType' ? meData.getMe.id : '';

    const onChangeTimeZone = (value: string) => {
        setSelectedValue(value);
        setSearchTerms('');
        onClose();

        changeTimeZone({
            variables: {
                agentId: agentId,
                timeZone: value,
            },
            onCompleted: async (data: any) => {
                if (data?.changeTimeZone?.__typename === 'SuccessType') {
                    toast({
                        title: data?.changeTimeZone.message,
                        isClosable: true,
                        status: 'success',
                    });

                    apolloClient.cache.modify({
                        id: `UserModelType:${userId}`,
                        fields: {
                            isAllowToStepper() {
                                return {
                                    isAllowToStepper: false,
                                    has_subscription: false,
                                    variant: variants.Default,
                                };
                            },
                        },
                    });

                    apolloClient.cache.modify({
                        id: `AgentType:${agentId}`,
                        fields: {
                            timeZone() {
                                return value;
                            },
                        },
                    });

                    try {
                        await fetchUserData();
                    } catch (err) {

                        // eslint-disable-next-line no-console
                        console.error('Error fetching user data after timezone change', err);
                    }

                    router(PATHS.MY_AGENT);
                } else {
                    toast({
                        title: data?.changeTimeZone.message,
                        isClosable: true,
                        status: 'error',
                    });
                }
            },
        });
    };

    return (
        <Box ref={boxRef} pos='relative'>
            <InputGroup
                onClick={() => {
                    if (!isOpen) {
                        setSearchTerms('');
                        onOpen();
                    }
                }}
            >
                <Input
                    focusBorderColor='gray.200'
                    value={isOpen ? searchTerms : selectedValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerms(e.target.value)}
                    onFocus={() => {
                        if (!isOpen) {
                            setSearchTerms('');
                            onOpen();
                        }
                    }}
                    placeholder='Click to browse or type to search time zones'
                />
                <InputRightElement>
                    {loading ? <Spinner size='xs' color='gray.400' /> : isOpen ? <ExpandUp /> : <ExpandDown />}
                </InputRightElement>
            </InputGroup>
            {isOpen && (
                <Box
                    maxH={200}
                    overflow='auto'
                    border='1px solid'
                    borderColor='gray.200'
                    backgroundColor='white'
                    pos='absolute'
                    w='full'
                    zIndex={1}
                >
                    {filteredTimeZones.map((value: string) => (
                        <Box
                            key={value}
                            onClick={() => onChangeTimeZone(value)}
                            cursor='pointer'
                            background={selectedValue === value ? '#5611f821' : 'white'}
                            _hover={{
                                bgColor: 'gray.200',
                            }}
                            p='10px'
                        >
                            {value}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default memo(TimeZone);
